// Arcane Boots Effect (Horizontal Sprint)
// Features: OPTIMIZED SCALING, 2 BOOTS, HORIZONTAL SLIDING SPRINT
// Exactly starting from avatar, sliding to the center, and sprinting horizontally.
// Highly optimized via offscreen canvas caching to maintain 60FPS.

import { Particle } from '../ParticleLayer';

const CONFIG = {
    scale: 0.75, // Scaled down by 50%
    materializeDuration: 800, // Doubled duration (2x slower)
    moveToCenterDuration: 600, // Doubled
    sprintDuration: 1600, // Doubled
    vanishDuration: 600, // Doubled
    paths: {
        boots: '/assets/effects/arcane_boots/boots.svg',
    }
};

const spriteCache: Record<string, HTMLImageElement> = {};
const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image();
        img.src = src;
        spriteCache[src] = img;
    }
    return spriteCache[src];
};

// --- FPS OPTIMIZATION: OFFSCREEN CACHE ---
const renderCache: Record<string, HTMLCanvasElement> = {};
const getCachedRenders = (img: HTMLImageElement) => {
    if (!renderCache['b1']) {
        const createCache = (filter?: string, shadowColor?: string) => {
            const c = document.createElement('canvas');
            const pad = 140; // Extra padding for large blur radii
            c.width = img.naturalWidth + pad;
            c.height = img.naturalHeight + pad;
            const ctx = c.getContext('2d')!;
            ctx.translate(c.width / 2, c.height / 2);
            if (filter) ctx.filter = filter;
            if (shadowColor) {
                ctx.shadowColor = shadowColor;
                ctx.shadowBlur = 40;
            }
            ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2, img.naturalWidth, img.naturalHeight);
            return c;
        };

        renderCache['b1'] = createCache(); // Front boot
        renderCache['b2'] = createCache('brightness(0.6)'); // Back boot
        renderCache['glow_blue'] = createCache(undefined, '#38bdf8'); // Trail afterimage blue
        renderCache['glow_purple'] = createCache(undefined, '#c084fc'); // Trail afterimage purple
        renderCache['glow_pink'] = createCache(undefined, '#e879f9'); // Trail afterimage pink
    }
    return renderCache as {
        b1: HTMLCanvasElement;
        b2: HTMLCanvasElement;
        glow_blue: HTMLCanvasElement;
        glow_purple: HTMLCanvasElement;
        glow_pink: HTMLCanvasElement;
    };
};

getSprite(CONFIG.paths.boots);

class ArcaneSpark {
    x: number; y: number;
    vx: number; vy: number;
    life: number; maxLife: number;
    size: number; rotation: number;
    rotSpeed: number; color: string;

    constructor(x: number, y: number, speedX: number, speedY: number) {
        this.x = x + (Math.random() - 0.5) * 60;
        this.y = y + (Math.random() - 0.5) * 60;

        // Eject backwards relative to sprint direction
        this.vx = speedX + (Math.random() - 0.5) * 10;
        this.vy = speedY + (Math.random() - 0.5) * 10;

        this.maxLife = 600 + Math.random() * 600; // Longer life
        this.life = this.maxLife;
        this.size = 2 + Math.random() * 8;
        this.rotation = Math.random() * Math.PI * 2;
        this.rotSpeed = (Math.random() - 0.5) * 0.2;

        const colors = ['#38bdf8', '#818cf8', '#c084fc', '#e879f9', '#ffffff']; // Arcane palette
        this.color = colors[Math.floor(Math.random() * colors.length)];
    }

    update(dt: number) {
        this.x += this.vx; this.y += this.vy;
        this.vx *= 0.92; this.vy *= 0.92; // fluid drag
        this.life -= dt;
        this.rotation += this.rotSpeed;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        const p = this.life / this.maxLife;

        ctx.save();
        ctx.globalAlpha = p;
        ctx.globalCompositeOperation = 'screen';
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);

        ctx.shadowColor = this.color;
        ctx.shadowBlur = 15 * p;
        ctx.fillStyle = this.color;

        // Draw an elegant shimmering 4-point star/diamond instead of circles or lines
        ctx.beginPath();
        ctx.moveTo(0, -this.size);
        ctx.lineTo(this.size * 0.3, 0);
        ctx.lineTo(0, this.size);
        ctx.lineTo(-this.size * 0.3, 0);
        ctx.closePath();
        ctx.fill();

        // Brilliant white core
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(0, -this.size * 0.4);
        ctx.lineTo(this.size * 0.1, 0);
        ctx.lineTo(0, this.size * 0.4);
        ctx.lineTo(-this.size * 0.1, 0);
        ctx.closePath();
        ctx.fill();

        ctx.restore();
    }
}

export type ArcaneBootsEffect = {
    kind: 'arcaneBoots'
    t: number
    tx: number; ty: number;
    data?: {
        phase: 'MATERIALIZE' | 'MOVING' | 'SPRINT' | 'VANISH'
        sparks: ArcaneSpark[]
        afterimages: { x: number, y: number, alpha: number, isB2: boolean, colorType: 'blue' | 'purple' | 'pink' }[]
    }
}

export const createArcaneBootsEffect = (tx: number, ty: number): ArcaneBootsEffect => ({
    kind: 'arcaneBoots', t: 0, tx, ty
});

export const renderArcaneBootsEffect = (
    eff: ArcaneBootsEffect,
    ctx: CanvasRenderingContext2D,
    spawnParticle: (p: Particle) => void
): boolean => {
    if (!eff.data) {
        eff.data = {
            phase: 'MATERIALIZE',
            sparks: [],
            afterimages: [],
        };
    }

    const d = eff.data;
    const dt = 16.7;

    const tMat = CONFIG.materializeDuration;
    const tMove = tMat + CONFIG.moveToCenterDuration;
    const tSprint = tMove + CONFIG.sprintDuration;
    const tTotal = tSprint + CONFIG.vanishDuration;

    if (eff.t < tMat) d.phase = 'MATERIALIZE';
    else if (eff.t < tMove) d.phase = 'MOVING';
    else if (eff.t < tSprint) d.phase = 'SPRINT';
    else d.phase = 'VANISH';

    const w = window.innerWidth;
    const h = window.innerHeight;
    const centerY = h / 2;

    ctx.save();

    let alpha = 1;
    let b1Scale = CONFIG.scale;
    let b2Scale = CONFIG.scale * 0.8; // Back boot smaller for perspective

    let b1X = eff.tx, b1Y = eff.ty;
    let b2X = eff.tx, b2Y = eff.ty;

    if (d.phase === 'MATERIALIZE') {
        const p = eff.t / tMat;
        const easeOut = 1 - Math.pow(1 - p, 4);

        alpha = p;
        b1Scale = CONFIG.scale * easeOut;
        b2Scale = CONFIG.scale * 0.8 * easeOut;

        b1Y = eff.ty - 50 * easeOut;
        b2X = eff.tx - 100 * easeOut;
        b2Y = eff.ty - 100 * easeOut;

        // Spawn gentle ambient sparks during materialize
        if (eff.t % 4 < 1 && p > 0.3) {
            d.sparks.push(new ArcaneSpark(b1X, b1Y, 0, -2));
        }

    } else if (d.phase === 'MOVING') {
        const p = (eff.t - tMat) / CONFIG.moveToCenterDuration;
        const easeIn = Math.pow(p, 2);

        // Move from avatar to screen center left edge
        const startX = eff.tx;
        const startY = eff.ty - 50;
        const endX = -300; // Left side of screen, wait deeper
        const endY = centerY;

        b1X = startX + (endX - startX) * easeIn;
        b1Y = startY + (endY - startY) * easeIn;

        b2X = b1X - 80 * easeIn;
        b2Y = b1Y - 40 * easeIn;

        if (eff.t % 6 < 2) {
            const colorType = Math.random() < 0.33 ? 'blue' : (Math.random() < 0.5 ? 'purple' : 'pink');
            d.afterimages.push({ x: b1X, y: b1Y, alpha: 0.3, isB2: false, colorType });
        }

    } else if (d.phase === 'SPRINT') {
        const p = (eff.t - tMove) / CONFIG.sprintDuration;
        const startX = -300;
        const endX = w + 400; // Sprint fully across screen

        // Velocity increases over time (Ease In effect even during sprint)
        const sprintProgress = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;

        b1X = startX + (endX - startX) * sprintProgress;
        b1Y = centerY;

        b2X = b1X - 80;
        b2Y = centerY - 40;

        // Elegant flowing bounce effect
        b1Y += Math.sin(sprintProgress * Math.PI * 18) * 15;
        b2Y += Math.cos(sprintProgress * Math.PI * 18) * 15;

        // Rich glowing motion trail (Overlapping boots with varied magic colors)
        if (eff.t % 4 < 2) {
            const colorType = Math.random() < 0.33 ? 'blue' : (Math.random() < 0.5 ? 'purple' : 'pink');
            d.afterimages.push({ x: b1X, y: b1Y, alpha: 0.7, isB2: false, colorType });
            d.afterimages.push({ x: b2X, y: b2Y, alpha: 0.6, isB2: true, colorType });
        }

        // Violent magical exhaust (Spark burst backward)
        if (Math.random() < 0.8) {
            // Calculate local speed by taking derivative of smooth progress loosely
            const speedX = -20 - Math.random() * 30; // Push particles left aggressively
            d.sparks.push(new ArcaneSpark(b1X - 60, b1Y, speedX, (Math.random() - 0.5) * 10));
            d.sparks.push(new ArcaneSpark(b2X - 50, b2Y, speedX, (Math.random() - 0.5) * 10));
        }

        // Very occasional glowing embers (longer life)
        if (Math.random() < 0.2) {
            spawnParticle({
                x: b1X - 80,
                y: b1Y + (Math.random() - 0.5) * 40,
                vx: -5 - Math.random() * 15,
                vy: (Math.random() - 0.5) * 15,
                life: 600 + Math.random() * 600,
                color: '#c084fc',
                size: 2 + Math.random() * 2
            });
        }

    } else if (d.phase === 'VANISH') {
        const p = (eff.t - tSprint) / CONFIG.vanishDuration;
        alpha = 1 - p;

        b1X = w + 400 + p * 100;
        b1Y = centerY;
        b2X = b1X - 80;
        b2Y = centerY - 40;

        if (alpha > 0.5 && Math.random() < 0.5) {
            d.sparks.push(new ArcaneSpark(b1X, b1Y, -10, 0));
        }
    }

    // Process Trail & Particle Physics
    d.afterimages.forEach(ai => ai.alpha -= 0.04); // Slower fade for a silky, lingering tail
    d.afterimages = d.afterimages.filter(ai => ai.alpha > 0);

    d.sparks.forEach(s => s.update(dt));

    // Draw Background magical sparks
    d.sparks.forEach(s => s.draw(ctx));

    // Draw Main Scene
    const img = getSprite(CONFIG.paths.boots);
    if (img.complete && img.naturalWidth > 0) {

        // Get lightning-fast cached frames
        const cache = getCachedRenders(img);
        const cw = cache.b1.width;
        const ch = cache.b1.height;

        const drawCachedBoot = (cacheCanvas: HTMLCanvasElement, x: number, y: number, scale: number, a: number) => {
            ctx.save();
            ctx.globalAlpha = a;
            ctx.translate(x, y);
            ctx.rotate(0.2); // Forward tilt
            ctx.scale(scale, scale);
            ctx.drawImage(cacheCanvas, -cw / 2, -ch / 2, cw, ch);
            ctx.restore();
        };

        // 1. Draw elegant glowing afterimage boot trails (instead of sticks/lines)
        ctx.save();
        ctx.globalCompositeOperation = 'screen';

        // Render in reverse order so brightest (youngest) are on top
        for (let i = d.afterimages.length - 1; i >= 0; i--) {
            const ai = d.afterimages[i];
            const cacheCanvas = cache[`glow_${ai.colorType}` as keyof typeof cache];
            drawCachedBoot(cacheCanvas, ai.x, ai.y, ai.isB2 ? b2Scale : b1Scale, ai.alpha * 0.8);

            // If very young, draw an extra bright core to make the front of the trail pop
            if (ai.alpha > 0.6) {
                drawCachedBoot(cache.b1, ai.x, ai.y, (ai.isB2 ? b2Scale : b1Scale) * 0.9, ai.alpha * 0.5);
            }
        }
        ctx.restore();

        // 2. Draw Boots foreground (if not completely vanished)
        if (alpha > 0) {
            drawCachedBoot(cache.b2, b2X, b2Y, b2Scale, alpha);
            drawCachedBoot(cache.b1, b1X, b1Y, b1Scale, alpha);
        }
    }

    ctx.restore();

    return eff.t < tTotal;
};
